import React, { useState, useMemo, useEffect } from "react";
import Card from "../ui/Card";
import { formatCount, pctOrDash } from "../../utils/formatters";
import { SetterMetrics, formatMinutes, getMetricColor } from "../../hooks/useSetterPerformance";
import type { SetterReplyTurnRow } from "../../hooks/useReplyTurnDistributionAll";
import type { SpeedToLeadRow } from "../../hooks/useSpeedToLead";
import { useSetterDailyBreakdown } from "../../hooks/useSetterDailyBreakdown";
import { useSetterSessionTimeline } from "../../hooks/useSetterSessionTimeline";
import { useSetterUnreplied } from "../../hooks/useSetterUnreplied";
import { useAnimGate } from "../../hooks/useAnimGate";
import { SessionClockChart } from "./SessionClockChart";
import { SessionMessagesModal } from "./SessionMessagesModal";
import { ConversationViewModal } from "./ConversationViewModal";
import { WorkPatternModal } from "./WorkPatternModal";
import { SetterMethodologyModal } from "./SetterMethodologyModal";
import { PreCallsModal } from "./PreCallsModal";
import { OrphanPreCallsModal } from "./OrphanPreCallsModal";
import { LostPreCallsModal } from "./LostPreCallsModal";
import SimpleMetricTooltip from "../ui/SimpleMetricTooltip";
import CountUp from "../ui/CountUp";
import type { ProofMetricType } from "../../types/core";

interface SetterScorecardProps {
  metrics: SetterMetrics;
  dayCount: number;
  onMetricClick: (
    metricType: ProofMetricType,
    setterId?: string,
    opts?: { defaultWindowMode?: "lifetime" | "window" }
  ) => void;
  // Work pattern inline props
  setterId: string;
  organizationId: string;
  setterIds: string[];
  /** Every setter rendered on this page, id + display name. Sourced from
   *  `metrics` in SetterScorecardGrid — the only place both live. `ghl_users`
   *  has no `name` column, so it cannot supply these. Feeds the modal switcher. */
  roster: { id: string; name: string }[];
  dateRange: { start: Date | null; end: Date | null };
  timezone: string;
  // Reply Turns card right-click → in-place lifetime/window flip.
  // Mode state lives in SetterScorecardGrid — one right-click flips EVERY
  // setter's card together. windowData is this setter's slice from
  // useReplyTurnDistributionAll (window mode), fetched lazily by the grid
  // after the first right-click anywhere.
  windowData?: SetterReplyTurnRow;
  rtCardMode?: "lifetime" | "window";
  onRtModeFlip?: () => void;
  windowLoading?: boolean;
  speedToLead?: SpeedToLeadRow;
  speedToLeadLoading?: boolean;
}

function perDay(value: number, days: number): string {
  if (days <= 0) return "—";
  const pd = value / days;
  return pd >= 10 ? `${Math.round(pd)}` : `${pd.toFixed(1)}`;
}

/** Log-scaled position on the speed track: 1 min → 0%, 24 hr → 100%. */
const speedPct = (mins: number): number =>
  Math.min(100, Math.max(0, (Math.log10(Math.max(mins, 1)) / Math.log10(1440)) * 100));

// Faint reference notches at 5 min / 30 min / 2 hr — the same anchors the
// proof-modal buckets and getMetricColor thresholds use.
const SPEED_TRACK_TICKS = [5, 30, 120];

/** Thin speed gauge under each SPEED metric. All four rows share the same
 *  log scale, so medians compare spatially across rows. Shorter fill = faster.
 *  colorClass = the metric's existing getMetricColor text class (reused as
 *  fill); omitted = neutral fill for unjudged metrics (Lead Reply Time). */
const SpeedTrack: React.FC<{ mins: number | null; colorClass?: string }> = ({
  mins,
  colorClass,
}) => {
  if (mins == null) return null;
  const fill = colorClass ? colorClass.replace("text-", "bg-") : "bg-black/20";
  return (
    <div className="relative h-[3px] rounded-full bg-black/[0.06] mt-2 overflow-hidden">
      {SPEED_TRACK_TICKS.map((t) => (
        <div
          key={t}
          className="absolute top-0 h-full w-px bg-black/10"
          style={{ left: `${speedPct(t)}%` }}
        />
      ))}
      <div
        className={`anim-fill absolute top-0 left-0 h-full rounded-full ${fill}`}
        style={{ width: `${Math.max(speedPct(mins), 2)}%` }}
      />
    </div>
  );
};

export const SetterScorecard: React.FC<SetterScorecardProps> = ({
  metrics,
  dayCount,
  onMetricClick,
  setterId,
  organizationId,
  setterIds,
  roster,
  dateRange,
  timezone,
  windowData,
  rtCardMode = "lifetime",
  onRtModeFlip,
  windowLoading,
  speedToLead,
  speedToLeadLoading,
}) => {
  const [showWorkPatternModal, setShowWorkPatternModal] = useState(false);
  const [showMethodologyModal, setShowMethodologyModal] = useState(false);
  const [showPreCallsModal, setShowPreCallsModal] = useState(false);
  const [showOrphanModal, setShowOrphanModal] = useState(false);
  const [showLostModal, setShowLostModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState<{
    start: string;
    end: string;
    label: string;
    sessionCount: number;
    /** The RPC's own org-tz-bucketed work_date for a day-row click, `null` for a
     *  clock-arc click. NOT derived from `allDates`, which is built with
     *  browser-local getters and drifts off the RPC's bucket for any viewer
     *  whose machine tz differs from the org's. */
    workDate: string | null;
  } | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<{
    conversationId: string;
    contactName: string;
    leadInstagram: string | null;
  } | null>(null);

  // Scroll-gates: each section's bar/pop animations hold until it scrolls into
  // view (anim-hold → anim-go, index.css), in sync with its <CountUp> numbers.
  const [activityRef, activityGate] = useAnimGate<HTMLDivElement>();
  const [messagingRef, messagingGate] = useAnimGate<HTMLDivElement>();
  const [speedRef, speedGate] = useAnimGate<HTMLDivElement>();
  const [replyTurnsRef, replyTurnsGate] = useAnimGate<HTMLDivElement>();
  const [followUpRef, followUpGate] = useAnimGate<HTMLDivElement>();
  const [convoSpanRef, convoSpanGate] = useAnimGate<HTMLDivElement>();

  const {
    data: dailyData,
    allRows: allDailyRows,
    isLoading: loadingDaily,
    fetch: fetchDaily,
  } = useSetterDailyBreakdown({ organizationId, setterId, setterIds, dateRange, timezone });

  const {
    data: sessionData,
    isLoading: loadingSessions,
    fetch: fetchSessions,
  } = useSetterSessionTimeline({
    organizationId,
    setterId,
    setterIds,
    dateRange,
    timezone,
  });

  useEffect(() => {
    if (setterId && organizationId && setterIds.length > 0) {
      fetchSessions();
    }
  }, [setterId, organizationId, setterIds.length, fetchSessions]);

  // Unreplied count — range-scoped, from the SAME proof RPC the drill-down modal
  // reads (get_setter_proof, p_metric_type='unreplied'). Sourcing both from one RPC
  // is what guarantees the number shown on the card equals the modal's row total.
  // Displayed on the Reply Turns card header (not a standalone tile), clickable.
  const { count: unrepliedCount, loading: unrepliedLoading } = useSetterUnreplied(
    organizationId,
    setterId,
    dateRange,
    timezone
  );

  const allDates = useMemo(() => {
    if (!dateRange.start || !dateRange.end) return [];
    const dates: string[] = [];
    const current = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    while (current <= end) {
      const y = current.getFullYear();
      const m = String(current.getMonth() + 1).padStart(2, "0");
      const d = String(current.getDate()).padStart(2, "0");
      dates.push(`${y}-${m}-${d}`);
      current.setDate(current.getDate() + 1);
    }
    return dates.reverse();
  }, [dateRange.start, dateRange.end]);

  // UNCONDITIONAL on purpose. The old `if (dailyData.length === 0)` guard fired
  // exactly once per mount, and SetterScorecardGrid keys each card by setter_id
  // alone — never by date range — so the hook's state survived a global
  // date-range change untouched. Open Work Pattern, close it, change the range,
  // reopen: every row read against the stale work_date values. The hook is
  // already stale-while-revalidate, so an unconditional call costs one cached
  // repaint, never a spinner.
  const handleOpenWorkPatternModal = async () => {
    await fetchDaily();
    setShowWorkPatternModal(true);
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (showMethodologyModal) setShowMethodologyModal(false);
        else if (showWorkPatternModal) setShowWorkPatternModal(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [showWorkPatternModal, showMethodologyModal]);

  // Color thresholds for responsiveness dots
  const avgReplyTimeColor = getMetricColor(metrics.avg_reply_time_median_mins, {
    good: 30,
    warn: 120,
  });
  const engagedResponseColor = getMetricColor(metrics.engaged_response_median_mins, {
    good: 5,
    warn: 15,
  });
  const speedToLeadColor = getMetricColor(speedToLead?.medianMins ?? null, { good: 30, warn: 120 });

  // Reply turn distribution — red (0) → green (19+) gradient, with lead/setter split
  const rtBuckets = [
    {
      key: "0",
      count: metrics.reply_turn_0,
      leadCount: metrics.reply_turn_0_lead,
      label: "0",
      color: "bg-red-500",
    },
    {
      key: "1",
      count: metrics.reply_turn_1,
      leadCount: metrics.reply_turn_1_lead,
      label: "1",
      color: "bg-red-400",
    },
    {
      key: "2",
      count: metrics.reply_turn_2,
      leadCount: metrics.reply_turn_2_lead,
      label: "2",
      color: "bg-orange-500",
    },
    {
      key: "3",
      count: metrics.reply_turn_3,
      leadCount: metrics.reply_turn_3_lead,
      label: "3",
      color: "bg-orange-400",
    },
    {
      key: "4",
      count: metrics.reply_turn_4,
      leadCount: metrics.reply_turn_4_lead,
      label: "4",
      color: "bg-amber-400",
    },
    {
      key: "5",
      count: metrics.reply_turn_5,
      leadCount: metrics.reply_turn_5_lead,
      label: "5",
      color: "bg-yellow-400",
    },
    {
      key: "6",
      count: metrics.reply_turn_6,
      leadCount: metrics.reply_turn_6_lead,
      label: "6",
      color: "bg-yellow-500",
    },
    {
      key: "7",
      count: metrics.reply_turn_7,
      leadCount: metrics.reply_turn_7_lead,
      label: "7",
      color: "bg-lime-400",
    },
    {
      key: "8",
      count: metrics.reply_turn_8,
      leadCount: metrics.reply_turn_8_lead,
      label: "8",
      color: "bg-lime-500",
    },
    {
      key: "9",
      count: metrics.reply_turn_9,
      leadCount: metrics.reply_turn_9_lead,
      label: "9",
      color: "bg-green-400",
    },
    {
      key: "10",
      count: metrics.reply_turn_10,
      leadCount: metrics.reply_turn_10_lead,
      label: "10",
      color: "bg-green-500",
    },
    {
      key: "11_12",
      count: metrics.reply_turn_11_12,
      leadCount: metrics.reply_turn_11_12_lead,
      label: "11-12",
      color: "bg-emerald-500",
    },
    {
      key: "13_18",
      count: metrics.reply_turn_13_18,
      leadCount: metrics.reply_turn_13_18_lead,
      label: "13-18",
      color: "bg-teal-500",
    },
    {
      key: "19plus",
      count: metrics.reply_turn_19plus,
      leadCount: metrics.reply_turn_19plus_lead,
      label: "19+",
      color: "bg-cyan-500",
    },
  ];
  const totalTierCount = rtBuckets.reduce((s, b) => s + b.count, 0);

  // ── Reply Turns card: lifetime ⇄ window display ──────────────────────────
  // rtBuckets[].key ("0".."10","11_12","13_18","19plus") is byte-identical to
  // the window hook's bucket labels, so window→inline is a clean key match that
  // preserves every color + display label and only swaps count/leadCount.
  const rtWindowActive = rtCardMode === "window";
  const rtDisplayBuckets =
    rtWindowActive && windowData
      ? rtBuckets.map((b) => {
          const w = windowData.buckets.find((wb) => wb.bucket === b.key);
          return { ...b, count: w?.count ?? 0, leadCount: w?.leadCount ?? 0 };
        })
      : rtBuckets;
  const rtDisplayTotal = rtWindowActive && windowData ? windowData.totalCount : totalTierCount;
  const rtDisplayAvg: number | null = rtWindowActive
    ? windowData
      ? Number(windowData.avgTurns.toFixed(1))
      : null
    : metrics.avg_reply_turns;
  // 0-4 vs 5+ reply-turn split. Derived from rtDisplayBuckets so it tracks the
  // lifetime⇄window flip for free. Keys "0".."4" are the shallow group; every
  // remaining key ("5".."10","11_12","13_18","19plus") is the 5+ group. The two
  // sum to rtDisplayTotal — self-checking against the "Total Conversations Active" line.
  const RT_LOW_KEYS = new Set(["0", "1", "2", "3", "4"]);
  const rtLowCount = rtDisplayBuckets.reduce(
    (s, b) => (RT_LOW_KEYS.has(b.key) ? s + b.count : s),
    0
  );
  const rtHighCount = rtDisplayBuckets.reduce(
    (s, b) => (RT_LOW_KEYS.has(b.key) ? s : s + b.count),
    0
  );
  // Window requested but the grid's single fetch hasn't landed for this setter yet.
  const rtWindowPending = rtWindowActive && !windowData && !!windowLoading;
  // 3-bar pulsing skeleton — reused by both the window-pending and the existing
  // cold-cache branch so the loading shape never diverges.
  const rtSkeleton = (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1.5">
        <span className="token-10 w-10 shrink-0" />
        <div
          className="flex-1 h-3 bg-black/[0.05] rounded anim-skeleton"
          style={{ width: "80%" }}
        />
      </div>
      <div className="flex items-center gap-1.5">
        <span className="token-10 w-10 shrink-0" />
        <div
          className="flex-1 h-3 bg-black/[0.05] rounded anim-skeleton"
          style={{ width: "60%" }}
        />
      </div>
      <div className="flex items-center gap-1.5">
        <span className="token-10 w-10 shrink-0" />
        <div
          className="flex-1 h-3 bg-black/[0.05] rounded anim-skeleton"
          style={{ width: "40%" }}
        />
      </div>
    </div>
  );

  // Follow-up persistence distribution
  // Conversation span distribution
  const csBuckets = [
    {
      key: "same_day",
      count: metrics.convo_span_same_day,
      color: "bg-gray-400",
      label: "Same Day",
    },
    { key: "1-2d", count: metrics.convo_span_1_2d, color: "bg-blue-400", label: "1-2d" },
    { key: "3-6d", count: metrics.convo_span_3_6d, color: "bg-green-500", label: "3-6d" },
    { key: "1-2w", count: metrics.convo_span_1_2w, color: "bg-yellow-400", label: "1-2w" },
    { key: "2w+", count: metrics.convo_span_2w_plus, color: "bg-orange-500", label: "2w+" },
  ].filter((b) => b.count > 0);
  const csTotal = csBuckets.reduce((sum, b) => sum + b.count, 0);

  const fpBuckets = [
    { key: "1d", count: metrics.followup_persistence_1d, color: "bg-yellow-400", label: "1d" },
    { key: "2d", count: metrics.followup_persistence_2d, color: "bg-lime-400", label: "2d" },
    { key: "3d", count: metrics.followup_persistence_3d, color: "bg-green-500", label: "3d" },
    { key: "4d", count: metrics.followup_persistence_4d, color: "bg-green-500", label: "4d" },
    { key: "5d", count: metrics.followup_persistence_5d, color: "bg-green-500", label: "5d" },
    { key: "6d", count: metrics.followup_persistence_6d, color: "bg-green-500", label: "6d" },
    { key: "7d", count: metrics.followup_persistence_7d, color: "bg-green-500", label: "7d" },
    { key: "7+d", count: metrics.followup_persistence_7plus, color: "bg-green-500", label: "7+" },
  ].filter((b) => b.count > 0);
  const fpTotal = fpBuckets.reduce((sum, b) => sum + b.count, 0);

  const chevron = (
    <svg
      className="w-3.5 h-3.5 text-glass-inactive flex-shrink-0 ml-auto"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
    </svg>
  );

  return (
    <Card className="p-0 overflow-hidden anim-lift">
      {/* ── Header: name | clock ────────────────────────────────── */}
      <div className="bg-gradient-to-r from-black/[0.02] to-black/[0.04] p-5 border-b border-black/5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="token-18 text-glass">{metrics.setter_name}</h3>
          </div>

          <div
            className="flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleOpenWorkPatternModal}
            title="Click to view full work pattern"
            data-testid="work-pattern-trigger"
          >
            {loadingSessions ? (
              <div className="w-[140px] h-[140px] flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
              </div>
            ) : sessionData.length > 0 ? (
              <SessionClockChart
                sessions={sessionData}
                dates={allDates}
                timezone={timezone}
                size={140}
                compact
              />
            ) : (
              <div className="w-[140px] h-[140px] rounded-full bg-black/5 flex items-center justify-center text-glass-muted token-11">
                No data
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Body: vertical section stack (top-to-bottom flow) ────── */}
      <div className="p-4">
        <div className="grid grid-cols-1 gap-px">
          {/* ACTIVITY section header */}
          <div className="pt-1 pb-1 px-3 token-10 font-semibold text-glass-muted uppercase tracking-wider">
            Activity
          </div>
          {/* 2x2 tile grid: Calls Pitched | Pre-Calls / Avg Hours | Deep Convos.
              Orphan/Lost chips stay a full-width strip between the two rows (col-span-2). */}
          <div ref={activityRef} className={`grid grid-cols-2 gap-px ${activityGate}`}>
            {/* New Conversations Assigned — real leads (lead sent their first-ever msg in range,
                ManyChat-excluded) routed to THIS setter. This is the funnel-entry volume — the
                "who's actually getting fed leads" number. Source: speedToLead.newLeads
                (= get_setter_speed_to_lead.new_leads), already fetched for the Speed to Lead tile,
                so this is presentation-only — NO new RPC/calc → NO cachebump. DELIBERATELY distinct
                from DEPTH's "Total Conversations Active" (= convos with ANY activity in range, new +
                re-pinged old). Full-width so the 2×2 hero grid below stays even. */}
            <div
              className="col-span-2 p-3 rounded-lg"
              data-testid="metric-new-conversations-assigned"
            >
              <div className="token-11 font-medium text-glass-muted mb-1">
                New Conversations Assigned
              </div>
              <div className="token-24 text-glass leading-none">
                {speedToLead ? (
                  <CountUp value={speedToLead.newLeads} />
                ) : speedToLeadLoading ? (
                  "…"
                ) : (
                  "—"
                )}
              </div>
              <div className="token-11 text-glass-muted mt-1">
                {speedToLead ? (
                  <>
                    <CountUp value={speedToLead.newLeads} format={(n) => perDay(n, dayCount)} />
                    /day · new leads routed in range
                  </>
                ) : (
                  "new leads routed in range"
                )}
              </div>
            </div>

            {/* Hero: Calls Pitched */}
            <div
              className="p-3 rounded-lg cursor-pointer anim-row"
              onClick={() => onMetricClick("calls_pitched", setterId)}
              data-testid="metric-row-calls_pitched"
            >
              <div className="token-11 font-medium text-glass-muted mb-1 flex items-center gap-1">
                Calls Pitched {chevron}
              </div>
              <div className="token-24 text-ds-accent leading-none">
                <CountUp value={metrics.calls_pitched} />
              </div>
              <div className="token-11 text-glass-muted mt-1">
                <CountUp value={metrics.calls_pitched} format={(n) => perDay(n, dayCount)} />
                /day · links sent
              </div>
            </div>

            {/* Hero: Pre-Calls */}
            <div
              className="p-3 rounded-lg cursor-pointer anim-row"
              onClick={() => setShowPreCallsModal(true)}
            >
              <div className="token-11 font-medium text-glass-muted mb-1 flex items-center gap-1">
                Pre-Calls {chevron}
              </div>
              <div className="token-24 text-ds-green leading-none">
                <CountUp value={metrics.precalls_total} />
              </div>
              <div className="token-11 text-glass-muted mt-1">
                <CountUp value={metrics.precalls_filled} /> filled after pitch
              </div>
              {/* Pre-Call Rate — moved here from its own OUTPUT-section row so the rate
                  sits with the pre-calls it measures, denominator named. Deep convos =
                  real_conversations (4+ reply turns, daily_dm_metrics.sql:339), NOT New DMs. */}
              <div className="token-11 text-glass-muted mt-0.5" data-testid="metric-precall-rate">
                {metrics.real_conversations > 0 ? (
                  <>
                    <CountUp
                      value={(metrics.precalls_filled / metrics.real_conversations) * 100}
                      format={(n) => `${n.toFixed(1)}%`}
                    />{" "}
                    of <CountUp value={metrics.real_conversations} /> deep convos
                  </>
                ) : (
                  "— rate · no deep convos"
                )}
              </div>
            </div>

            {/* Orphan + Lost chips — full-width row inside the ACTIVITY section */}
            {(metrics.orphan_pre_calls_count > 0 ||
              metrics.orphan_pre_calls_error ||
              metrics.lost_pre_calls_count > 0 ||
              metrics.lost_pre_calls_error) && (
              <div className="col-span-2 flex items-center gap-3 flex-wrap px-3">
                {(metrics.orphan_pre_calls_count > 0 || metrics.orphan_pre_calls_error) && (
                  <div
                    className="anim-tap-sm token-11 text-ds-amber cursor-pointer hover:underline flex items-center gap-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!metrics.orphan_pre_calls_error) {
                        setShowOrphanModal(true);
                      }
                    }}
                  >
                    {metrics.orphan_pre_calls_error ? (
                      "— orphans (load failed)"
                    ) : (
                      <>
                        <CountUp value={metrics.orphan_pre_calls_count} /> orphan
                        {metrics.orphan_pre_calls_count === 1 ? "" : "s"} →
                      </>
                    )}
                  </div>
                )}
                {(metrics.lost_pre_calls_count > 0 || metrics.lost_pre_calls_error) && (
                  <div className="token-11 text-ds-red flex items-center gap-1">
                    {/* inline-block: transforms don't apply to inline elements, .anim-tap-sm needs a box */}
                    <span
                      className="anim-tap-sm inline-block cursor-pointer hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!metrics.lost_pre_calls_error) {
                          setShowLostModal(true);
                        }
                      }}
                    >
                      {metrics.lost_pre_calls_error ? (
                        "— lost (load failed)"
                      ) : (
                        <>
                          <CountUp value={metrics.lost_pre_calls_count} /> lost pre-calls →
                        </>
                      )}
                    </span>
                    {!metrics.lost_pre_calls_error && (
                      <SimpleMetricTooltip
                        title="Lost Pre-Calls"
                        formula="setter's pre-calls → SCO=lost AND no later win"
                        description="Pre-calls this setter booked where the closer marked the call 'lost', net of leads who later came back and won (PIF or CPP). Differs from the raw 'Lost' count on the Transactions page, which is the unfiltered SCO event log — Transactions counts every lost row regardless of attribution or later recovery, while DM Analytics gates on (a) a matching pre-call attributed to this setter and (b) no subsequent win on the same email."
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Avg Hours/Day */}
            <div className="p-3 rounded-lg">
              <div className="token-11 font-medium text-glass-muted mb-1">Avg Hours/Day</div>
              <div className="token-15 text-glass leading-none">
                {metrics.avg_work_mins_per_day != null ? (
                  <CountUp
                    value={metrics.avg_work_mins_per_day}
                    format={(n) => `${(n / 60).toFixed(1)} hr`}
                  />
                ) : (
                  "—"
                )}
              </div>
            </div>

            {/* Conversations Worked/Day — work_date attribution (setter_daily_performance) */}
            {/* Phase 4 note: per-setter drift skipped — first_msg_date 4+ count not in
              getter_daily_performance. Requires SQL extension to get_setter_performance_fast.
              Flagged for Phase 5 follow-up. Org-level drift renders on DMHealthTab. */}
            <div className="p-3 rounded-lg" data-testid="metric-tile-deep-convos">
              <div className="token-11 font-medium text-glass-muted mb-1">Deep Convos/Day</div>
              <div className="token-15 text-glass leading-none">
                <span data-testid="metric-tile-deep-convos-value">
                  <CountUp value={metrics.real_conversations} format={(n) => perDay(n, dayCount)} />
                </span>
              </div>
              <div className="token-11 text-glass-muted mt-1">conv worked</div>
            </div>

            {/* S100 setter-performance tiles (dm-setter-perf-tiles) — 4 now()-anchored
                tiles answering "are setters leaving money on the table". DELIBERATELY
                range-INDEPENDENT: each sub-line names its own fixed window and the
                date-range picker does NOT move them (CTX-nowanchored). T1/T2 are rates
                (pctOrDash → "—" when the denominator is 0); T3/T4 are raw counts that
                render "0", never a dash. Source: get_setter_cross_day_metrics. */}
            {/* T1 — Live Leak Rate: warm leads this week that got zero human reply */}
            <div className="p-3 rounded-lg" data-testid="metric-tile-leak">
              <div className="token-11 font-medium text-glass-muted mb-1">Live Leak Rate</div>
              <div className="token-15 text-ds-red leading-none">
                <span data-testid="metric-tile-leak-value">
                  {pctOrDash(metrics.leak_untouched_7d, metrics.leak_engaged_7d)}
                </span>
              </div>
              <div className="token-11 text-glass-muted mt-1">
                this week · warm leads never answered
              </div>
            </div>
            {/* T2 — Depth-Conversion: engaged leads (30d) that reached a 4-turn talk */}
            <div className="p-3 rounded-lg" data-testid="metric-tile-depth">
              <div className="token-11 font-medium text-glass-muted mb-1">Depth-Conversion</div>
              <div className="token-15 text-ds-green leading-none">
                <span data-testid="metric-tile-depth-value">
                  {pctOrDash(metrics.depth_deep_30d, metrics.depth_engaged_30d)}
                </span>
              </div>
              <div className="token-11 text-glass-muted mt-1">
                last 30 days · reached a 4-turn talk
              </div>
            </div>
            {/* T3 — Deep Cliff-Fall: deep leads that went dark 24-48h ago, still owed */}
            <div className="p-3 rounded-lg" data-testid="metric-tile-cliff">
              <div className="token-11 font-medium text-glass-muted mb-1">Deep Cliff-Fall</div>
              <div className="token-15 text-ds-red leading-none">
                <span data-testid="metric-tile-cliff-value">
                  <CountUp value={metrics.cliff_deep_24_48h} />
                </span>
              </div>
              <div className="token-11 text-glass-muted mt-1">
                last 24-48h · deep leads dying on IG
              </div>
            </div>
            {/* T4 — Actionable-Deep Owed: deep owed leads still inside the 24h IG window */}
            <div className="p-3 rounded-lg" data-testid="metric-tile-actionable">
              <div className="token-11 font-medium text-glass-muted mb-1">Actionable-Deep Owed</div>
              <div className="token-15 text-ds-accent leading-none">
                <span data-testid="metric-tile-actionable-value">
                  <CountUp value={metrics.actionable_deep_lt24h} />
                </span>
              </div>
              <div className="token-11 text-glass-muted mt-1">reachable today · clear now</div>
            </div>
          </div>

          {/* OUTPUT section header */}
          <div className="mt-2 pt-3 pb-1 px-3 token-10 font-semibold text-glass-muted uppercase tracking-wider border-t border-black/[0.06]">
            Output
          </div>
          {/* Sub-hero: Words Typed — total words the setter sent in range. Same
              total_words_sent field the Messaging card divides for words/reply, so this
              is presentation-only: NO new RPC/calc → NO cachebump. Mirrors the "New
              Conversations Assigned" sub-hero (full-width, token-24, /day subline). */}
          <div className="p-3 rounded-lg" data-testid="metric-total-words-sent">
            <div className="token-11 font-medium text-glass-muted mb-1">Words Typed</div>
            <div className="token-24 text-glass leading-none">
              <CountUp
                value={metrics.total_words_sent}
                format={(n) => Math.round(n).toLocaleString()}
              />
            </div>
            {/* /day only for multi-day ranges — on a single day the average equals
                the headline total, so show a plain descriptor instead of repeating it. */}
            <div className="token-11 text-glass-muted mt-1">
              {dayCount > 1 ? (
                <>
                  <CountUp
                    value={metrics.total_words_sent / dayCount}
                    format={(n) => Math.round(n).toLocaleString()}
                  />
                  /day · total words sent
                </>
              ) : (
                "total words sent"
              )}
            </div>
          </div>
          {/* Messaging — replaces the old single Msgs Sent tile.
              Surfaces message-style bias hidden by /hr alone (Patil sends 12w fragments,
              Alice writes 24w paragraphs — both at ~80-150/hr, but very different work).
              NOT clickable (Jun 11 ruling): its popup was just the reply-turn proof
              modal in window mode — redundant with the Reply Turns card below. */}
          <div
            ref={messagingRef}
            className={`p-3 rounded-lg ${messagingGate}`}
            data-testid="metric-card-messaging"
          >
            <div className="token-11 font-medium text-glass-muted mb-2 flex items-center gap-1">
              Messaging
            </div>
            <div className="grid grid-cols-4 gap-2">
              {/* Total + /hr */}
              <div>
                <div className="token-15 text-glass leading-none">
                  <CountUp
                    value={metrics.total_messages_sent}
                    format={(n) => Math.round(n).toLocaleString()}
                  />
                </div>
                <div className="token-11 text-glass-muted mt-1 whitespace-nowrap">
                  {metrics.total_work_minutes != null && metrics.total_work_minutes > 0 ? (
                    <>
                      <CountUp
                        value={metrics.total_messages_sent / (metrics.total_work_minutes / 60)}
                      />
                      /hr
                    </>
                  ) : (
                    "—"
                  )}
                </div>
              </div>
              {/* Avg words per reply turn — denominator is total_reply_turns (the 218
                  "replies" beside it), NOT total_messages_sent. Bare "words" read as
                  per-reply but computed per-msg before Sep 2; label now says the lane. */}
              <div>
                <div className="token-15 text-glass leading-none">
                  {metrics.total_reply_turns > 0 ? (
                    <CountUp value={metrics.total_words_sent / metrics.total_reply_turns} />
                  ) : (
                    "—"
                  )}
                </div>
                <div className="token-11 text-glass-muted mt-1 whitespace-nowrap">words/reply</div>
              </div>
              {/* Msgs per reply turn */}
              <div>
                <div className="token-15 text-glass leading-none">
                  {metrics.total_messages_sent === 0 || metrics.total_reply_turns === 0 ? (
                    "—"
                  ) : (
                    <CountUp
                      value={metrics.total_messages_sent / metrics.total_reply_turns}
                      format={(n) => n.toFixed(1)}
                    />
                  )}
                </div>
                <div className="token-11 text-glass-muted mt-1 whitespace-nowrap">
                  {metrics.total_messages_sent > 0 && metrics.total_reply_turns === 0
                    ? "outbound"
                    : "per turn"}
                </div>
              </div>
              {/* Replies made (in-window setter reply turns) */}
              <div>
                <div className="token-15 text-glass leading-none">
                  <CountUp value={metrics.total_reply_turns} format={formatCount} />
                </div>
                <div className="token-11 text-glass-muted mt-1 whitespace-nowrap">replies</div>
              </div>
            </div>
          </div>

          {/* Pre-Call Rate moved onto the Pre-Calls tile in the ACTIVITY section
              (Option A, Aug 19) — it now reads "X% of N deep convos" beside the
              pre-calls it measures, instead of floating as its own row here. */}

          {/* SPEED section header */}
          <div className="mt-2 pt-3 pb-1 px-3 token-10 font-semibold text-glass-muted uppercase tracking-wider border-t border-black/[0.06]">
            Speed
          </div>
          {/* 2x2 tile grid: Speed to Lead | Setter Reply / Engaged | Lead Reply */}
          <div ref={speedRef} className={`grid grid-cols-2 gap-px ${speedGate}`}>
            {/* Speed to Lead — first-touch on NEW leads (v2). Clickable → proof modal (P1-SPEED-PROOF). */}
            <div
              className="relative p-3 rounded-lg cursor-pointer anim-row"
              onClick={() => onMetricClick("speed_to_lead", setterId)}
              data-testid="metric-row-speed_to_lead"
            >
              {/* Chevrons sit out of flow at top-right — half-width labels need the room */}
              <span className="absolute top-3 right-3">{chevron}</span>
              <div className="token-11 font-medium text-glass-muted mb-1 flex items-center gap-1.5">
                Speed to Lead
                <span
                  className={`w-1.5 h-1.5 rounded-full ${speedToLeadColor.replace("text-", "bg-")}`}
                />
              </div>
              <div className="token-15 text-glass leading-none">
                {speedToLead ? (
                  speedToLead.medianMins != null ? (
                    <CountUp value={speedToLead.medianMins} format={formatMinutes} />
                  ) : (
                    formatMinutes(speedToLead.medianMins)
                  )
                ) : speedToLeadLoading ? (
                  "…"
                ) : (
                  "—"
                )}
              </div>
              <SpeedTrack mins={speedToLead?.medianMins ?? null} colorClass={speedToLeadColor} />
              <div className="token-11 text-glass-muted mt-0.5">median first touch</div>
              <div className="token-11 text-glass-muted mt-0.5" data-testid="speed-to-lead-subline">
                {!speedToLead ? (
                  "0 new leads in range"
                ) : speedToLead.replied === 0 ? (
                  <>
                    no replies · <CountUp value={speedToLead.newLeads} /> leads
                  </>
                ) : (
                  <>
                    <CountUp value={(100 * speedToLead.sub5) / speedToLead.replied} />
                    % &lt;5min · <CountUp value={speedToLead.newLeads} /> leads
                  </>
                )}
              </div>
            </div>
            {/* Setter Reply Time */}
            <div
              className="relative p-3 rounded-lg cursor-pointer anim-row"
              onClick={() => onMetricClick("avg_reply_time", setterId)}
              data-testid="metric-row-avg_reply_time"
            >
              <span className="absolute top-3 right-3">{chevron}</span>
              <div className="token-11 font-medium text-glass-muted mb-1 flex items-center gap-1.5">
                Setter Reply Time
                <span
                  className={`w-1.5 h-1.5 rounded-full ${avgReplyTimeColor.replace("text-", "bg-")}`}
                />
              </div>
              <div className="token-15 text-glass leading-none">
                {metrics.avg_reply_time_median_mins != null ? (
                  <CountUp value={metrics.avg_reply_time_median_mins} format={formatMinutes} />
                ) : (
                  formatMinutes(metrics.avg_reply_time_median_mins)
                )}
              </div>
              <SpeedTrack
                mins={metrics.avg_reply_time_median_mins}
                colorClass={avgReplyTimeColor}
              />
              <div className="token-11 text-glass-muted mt-0.5">median, all replies</div>
            </div>

            {/* Engaged Response */}
            <div
              className="relative p-3 rounded-lg cursor-pointer anim-row"
              onClick={() => onMetricClick("engaged_response", setterId)}
              data-testid="metric-row-engaged_response"
            >
              <span className="absolute top-3 right-3">{chevron}</span>
              <div className="token-11 font-medium text-glass-muted mb-1 flex items-center gap-1.5">
                Engaged Response
                <span
                  className={`w-1.5 h-1.5 rounded-full ${engagedResponseColor.replace("text-", "bg-")}`}
                />
              </div>
              <div className="token-15 text-glass leading-none">
                {metrics.engaged_response_median_mins != null ? (
                  <CountUp value={metrics.engaged_response_median_mins} format={formatMinutes} />
                ) : (
                  formatMinutes(metrics.engaged_response_median_mins)
                )}
              </div>
              <SpeedTrack
                mins={metrics.engaged_response_median_mins}
                colorClass={engagedResponseColor}
              />
              <div className="token-11 text-glass-muted mt-0.5">median, active chats</div>
            </div>

            {/* Lead Reply Time */}
            <div
              className="relative p-3 rounded-lg cursor-pointer anim-row"
              onClick={() => onMetricClick("lead_reply_time", setterId)}
              data-testid="metric-row-lead_reply_time"
            >
              <span className="absolute top-3 right-3">{chevron}</span>
              <div className="token-11 font-medium text-glass-muted mb-1 flex items-center gap-1">
                Lead Reply Time
              </div>
              <div className="token-15 text-glass leading-none">
                {metrics.lead_reply_median_mins != null ? (
                  <CountUp value={metrics.lead_reply_median_mins} format={formatMinutes} />
                ) : (
                  formatMinutes(metrics.lead_reply_median_mins)
                )}
              </div>
              <SpeedTrack mins={metrics.lead_reply_median_mins} />
              <div className="token-11 text-glass-muted mt-0.5">how fast leads respond</div>
            </div>
          </div>

          {/* DEPTH section header */}
          <div className="mt-2 pt-3 pb-1 px-3 token-10 font-semibold text-glass-muted uppercase tracking-wider border-t border-black/[0.06]">
            Depth
          </div>
          {/* Reply Turn Distribution — full width.
              Left-click → proof modal. Right-click → flip ALL setters' cards
              between lifetime totals and in-window reply turns (no modal);
              mode state lives in SetterScorecardGrid. */}
          <div
            ref={replyTurnsRef}
            className={`p-3 rounded-lg cursor-pointer anim-row ${replyTurnsGate}`}
            onClick={() => onMetricClick("reply_turn_distribution", setterId)}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRtModeFlip?.();
            }}
            title="Left-click: open detail · Right-click: toggle lifetime / window"
            data-testid="metric-row-reply_turn_distribution"
          >
            <div className="token-11 font-medium text-glass-muted mb-2 flex items-center gap-1">
              Reply Turns {chevron}
              <span
                data-testid="rt-card-mode"
                className={`ml-auto token-9 px-1.5 py-0.5 rounded ${
                  rtWindowActive
                    ? "bg-ds-accent/15 text-ds-accent"
                    : "bg-black/[0.05] text-glass-muted"
                }`}
              >
                {rtWindowActive ? "window" : "lifetime"}
              </span>
            </div>
            {rtWindowPending ? (
              rtSkeleton
            ) : rtDisplayTotal > 0 ? (
              // .anim-pop: skeleton→content is a remount, so the bars fade up when data lands
              <div className="anim-pop">
                {/* Summary line */}
                <div className="flex items-center justify-between token-10 text-glass-muted mb-1.5">
                  <span>
                    Total Conversations Active:{" "}
                    <span className="font-medium text-glass-secondary">
                      <CountUp value={rtDisplayTotal} />
                    </span>
                    {rtDisplayAvg != null && (
                      <span className="ml-2">
                        avg:{" "}
                        <span className="font-medium text-glass-secondary">
                          <CountUp
                            value={rtDisplayAvg}
                            format={(n) => String(Math.round(n * 10) / 10)}
                          />
                        </span>
                      </span>
                    )}
                  </span>
                </div>
                {/* 0-4 vs 5+ reply-turn split tally */}
                <div className="flex items-center gap-3 token-10 text-glass-muted mb-1.5">
                  <span>
                    0-4 turns:{" "}
                    <span className="font-medium text-glass-secondary">
                      <CountUp value={rtLowCount} />
                    </span>
                  </span>
                  <span>
                    5+ turns:{" "}
                    <span className="font-medium text-glass-secondary">
                      <CountUp value={rtHighCount} />
                    </span>
                  </span>
                  <span>
                    5+ rate:{" "}
                    <span className="font-medium text-glass-secondary">
                      <CountUp
                        value={(rtHighCount / rtDisplayTotal) * 100}
                        format={(n) => `${n.toFixed(1)}%`}
                      />
                    </span>
                  </span>
                  {/* Unreplied — proof-sourced (range-scoped), the SAME population as
                      the drill-down it opens, so the number always equals the modal
                      total. Clickable; stopPropagation so it doesn't fire the card's
                      own reply-turn modal. Range-scoped by design: "who's waiting now,
                      among leads active in range", not a lifetime pile. */}
                  {/* Red-dot pill — deliberately styled as an obvious clickable
                      chip (not inline text) so it reads as a button, not a stat.
                      stopPropagation keeps the card's own reply-turn modal from
                      firing. Opens the unreplied hours drill-down. S97 fix. */}
                  <button
                    type="button"
                    className="ml-auto inline-flex items-center gap-1.5 token-10 font-semibold pl-2 pr-1.5 py-1 rounded-full bg-ds-red/10 text-ds-red ring-1 ring-ds-red/25 hover:bg-ds-red/20 hover:ring-ds-red/45 transition-colors cursor-pointer"
                    title="Conversations where the lead's last message got no setter reply. Click to see them bucketed by hours waited."
                    data-testid="rt-unreplied-total"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMetricClick("unreplied", setterId);
                    }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full bg-ds-red flex-shrink-0"
                      aria-hidden
                    />
                    <span>Unreplied</span>
                    <span>
                      {unrepliedCount != null ? (
                        <CountUp value={unrepliedCount} />
                      ) : unrepliedLoading ? (
                        "…"
                      ) : (
                        "—"
                      )}
                    </span>
                    <svg
                      className="w-3 h-3 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
                {/* Mini horizontal bar chart — same format as proof modal */}
                {(() => {
                  const maxCount = Math.max(...rtDisplayBuckets.map((b) => b.count), 1);
                  // 19+ at top, 0 at bottom; pre-filter so the stagger index is contiguous
                  const reversed = [...rtDisplayBuckets].reverse().filter((b) => b.count > 0);
                  return (
                    <div className="space-y-0.5">
                      {reversed.map((b, idx) => {
                        const barPct = (b.count / maxCount) * 100;
                        const leadPct = b.count > 0 ? (b.leadCount / b.count) * 100 : 0;
                        return (
                          <div key={b.key} className="flex items-center gap-1.5">
                            <span className="token-10 text-glass-muted w-10 text-right shrink-0 font-mono">
                              {b.label}
                            </span>
                            <div className="flex-1 h-3 bg-black/[0.03] rounded overflow-hidden">
                              {/* anim-fill + stagger: bars wipe in top-to-bottom as data lands */}
                              <div
                                className="anim-fill flex h-full"
                                style={{
                                  width: `${barPct}%`,
                                  animationDelay: `${idx * 45}ms`,
                                }}
                              >
                                <div
                                  className={`${b.color} h-full`}
                                  style={{ width: `${leadPct}%` }}
                                />
                                <div
                                  className={`${b.color} opacity-30 h-full`}
                                  style={{ width: `${100 - leadPct}%` }}
                                />
                              </div>
                            </div>
                            <span className="token-10 font-mono font-medium text-glass-secondary w-8 text-right shrink-0">
                              {/* delay matches the row's .anim-fill stagger so number + bar land together */}
                              <CountUp value={b.count} delay={idx * 45} />
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            ) : metrics.total_reply_turns > 0 && !rtWindowActive ? (
              // Skeleton — metrics.total_reply_turns is hydrated from daily-rows cache but the
              // reply_turn_* buckets (rtBuckets) are zero until get_reply_turn_distribution RPC
              // fills them. Same asymmetric cache restoration pattern as S30 contactIdMap.
              // Show pulsing bars so the user knows data is loading, not absent.
              rtSkeleton
            ) : (
              <span className="token-14 text-glass-muted">
                {rtWindowActive ? "No in-window reply turns" : "—"}
              </span>
            )}
          </div>

          {/* Follow-up Persistence — full width */}
          {/* 3-way gate: skeleton while cross_day pending; bars if hydrated+data; hide if hydrated+zero */}
          {!metrics.cross_day_hydrated ? (
            <div className="p-3 rounded-lg" data-testid="fp-skeleton">
              <div className="token-11 font-medium text-glass-muted mb-2">
                Follow-up Persistence
              </div>
              <div className="h-1.5 rounded-full bg-black/10 anim-skeleton" />
            </div>
          ) : fpTotal > 0 ? (
            <div
              ref={followUpRef}
              className={`p-3 rounded-lg cursor-pointer anim-row ${followUpGate}`}
              onClick={() => onMetricClick("followup_persistence", setterId)}
              data-testid="metric-row-followup_persistence"
            >
              <div className="token-11 font-medium text-glass-muted mb-2 flex items-center gap-1">
                Follow-up Persistence {chevron}
              </div>
              <div className="flex h-1.5 rounded-full overflow-hidden">
                {/* anim-fill + stagger: segments assemble left → right */}
                {fpBuckets.map((b, idx) => (
                  <div
                    key={b.key}
                    className={`anim-fill ${b.color}`}
                    style={{
                      width: `${(b.count / fpTotal) * 100}%`,
                      animationDelay: `${idx * 80}ms`,
                    }}
                  />
                ))}
              </div>
              <div className="flex gap-2 mt-1.5 token-11 font-medium flex-wrap">
                {fpBuckets.map((b, idx) => (
                  <span key={b.key} className="text-glass-muted">
                    <span className="text-glass-secondary">
                      <CountUp value={b.count} delay={idx * 80} />
                    </span>{" "}
                    {b.label}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {/* Conversation Span — full width */}
          {/* 3-way gate: skeleton while cross_day pending; bars if hydrated+data; hide if hydrated+zero */}
          {!metrics.cross_day_hydrated ? (
            <div className="p-3 rounded-lg" data-testid="cs-skeleton">
              <div className="token-11 font-medium text-glass-muted mb-2">Conversation Span</div>
              <div className="h-1.5 rounded-full bg-black/10 anim-skeleton" />
            </div>
          ) : csTotal > 0 ? (
            <div
              ref={convoSpanRef}
              className={`p-3 rounded-lg cursor-pointer anim-row ${convoSpanGate}`}
              onClick={() => onMetricClick("conversation_span", setterId)}
              data-testid="metric-row-conversation_span"
            >
              <div className="token-11 font-medium text-glass-muted mb-2 flex items-center gap-1">
                Conversation Span{" "}
                <span className="text-glass-inactive ml-1">
                  avg{" "}
                  {metrics.avg_convo_span_days != null ? (
                    <>
                      <CountUp
                        value={metrics.avg_convo_span_days}
                        format={(n) => String(Math.round(n * 10) / 10)}
                      />
                      d
                    </>
                  ) : (
                    "—"
                  )}
                </span>{" "}
                {chevron}
              </div>
              <div className="flex h-1.5 rounded-full overflow-hidden">
                {/* anim-fill + stagger: segments assemble left → right */}
                {csBuckets.map((b, idx) => (
                  <div
                    key={b.key}
                    className={`anim-fill ${b.color}`}
                    style={{
                      width: `${(b.count / csTotal) * 100}%`,
                      animationDelay: `${idx * 80}ms`,
                    }}
                  />
                ))}
              </div>
              <div className="flex gap-2 mt-1.5 token-11 font-medium flex-wrap">
                {csBuckets.map((b, idx) => (
                  <span key={b.key} className="text-glass-muted">
                    <span className="text-glass-secondary">
                      <CountUp value={b.count} delay={idx * 80} />
                    </span>{" "}
                    {b.label}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        {/* Methodology link. The testid belongs on THIS button, not on the modal
            element further down — a testid on the modal never receives the opening
            click, which is what R1 needs. */}
        <button
          data-testid="methodology-trigger"
          onClick={() => setShowMethodologyModal(true)}
          className="anim-tap w-full text-center token-11 text-glass-inactive hover:text-glass-muted transition-colors pt-3 pb-1"
        >
          How are these calculated?
        </button>
      </div>

      {/* ── Modals ──────────────────────────────────────────────── */}

      {selectedSession && (
        <SessionMessagesModal
          isOpen={true}
          onClose={() => setSelectedSession(null)}
          organizationId={organizationId}
          setterId={setterId}
          setterName={metrics.setter_name}
          sessionStart={selectedSession.start}
          sessionEnd={selectedSession.end}
          label={selectedSession.label}
          sessionCount={selectedSession.sessionCount}
          timezone={timezone}
          roster={roster}
          allDailyRows={allDailyRows}
          workDate={selectedSession.workDate}
          onConversationClick={(conversationId, contactName, leadInstagram) =>
            setSelectedConversation({ conversationId, contactName, leadInstagram })
          }
        />
      )}

      {selectedConversation && (
        <ConversationViewModal
          isOpen={true}
          onClose={() => setSelectedConversation(null)}
          conversationId={selectedConversation.conversationId}
          contactName={selectedConversation.contactName}
          leadInstagram={selectedConversation.leadInstagram}
          timezone={timezone}
          organizationId={organizationId}
          activeDateRange={
            dateRange.start && dateRange.end ? { start: dateRange.start, end: dateRange.end } : null
          }
        />
      )}

      <WorkPatternModal
        isOpen={showWorkPatternModal}
        onClose={() => setShowWorkPatternModal(false)}
        setterName={metrics.setter_name}
        dateRange={dateRange}
        timezone={timezone}
        loadingDaily={loadingDaily}
        dailyData={dailyData}
        sessionData={sessionData}
        allDates={allDates}
        onSessionClick={(start, end, label, sessionCount, workDate) =>
          setSelectedSession({ start, end, label, sessionCount, workDate })
        }
      />

      <SetterMethodologyModal
        isOpen={showMethodologyModal}
        onClose={() => setShowMethodologyModal(false)}
      />

      <PreCallsModal
        isOpen={showPreCallsModal}
        onClose={() => setShowPreCallsModal(false)}
        organizationId={organizationId}
        setterId={setterId}
        setterName={metrics.setter_name}
        dateRange={dateRange}
        timezone={timezone}
      />

      <OrphanPreCallsModal
        isOpen={showOrphanModal}
        onClose={() => setShowOrphanModal(false)}
        organizationId={organizationId}
        setterId={setterId}
        setterName={metrics.setter_name}
        dateRange={dateRange}
        timezone={timezone}
      />

      <LostPreCallsModal
        isOpen={showLostModal}
        onClose={() => setShowLostModal(false)}
        organizationId={organizationId}
        setterId={setterId}
        setterName={metrics.setter_name}
        dateRange={dateRange}
        timezone={timezone}
      />
    </Card>
  );
};
